import { useEffect, useRef, useState } from 'react';

interface TurnstileCaptchaProps {
  onVerify: (token: string | null) => void;
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: TurnstileOptions) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

interface TurnstileOptions {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
}

// Get sitekey from env or use test key in development
const TURNSTILE_SITEKEY = import.meta.env.VITE_TURNSTILE_SITEKEY || '1x00000000000000000000AA';

export default function TurnstileCaptcha({ onVerify }: TurnstileCaptchaProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // In development without Turnstile configured, auto-verify
    if (import.meta.env.DEV && TURNSTILE_SITEKEY === '1x00000000000000000000AA') {
      setIsLoading(false);
      // Auto-verify in dev mode after a brief delay
      const timer = setTimeout(() => {
        onVerify('dev-mode-token');
      }, 500);
      return () => clearTimeout(timer);
    }

    // Load Turnstile script if not already loaded
    if (!window.turnstile) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
      script.async = true;
      script.defer = true;

      window.onTurnstileLoad = () => {
        renderWidget();
      };

      document.head.appendChild(script);

      return () => {
        if (widgetIdRef.current && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current);
        }
        document.head.removeChild(script);
        delete window.onTurnstileLoad;
      };
    } else {
      renderWidget();
    }

    function renderWidget() {
      if (!containerRef.current || !window.turnstile) return;

      setIsLoading(false);

      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITEKEY,
        callback: (token: string) => {
          onVerify(token);
        },
        'error-callback': () => {
          onVerify(null);
        },
        'expired-callback': () => {
          onVerify(null);
        },
        theme: 'auto',
        size: 'normal',
      });
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, [onVerify]);

  // Dev mode placeholder
  if (import.meta.env.DEV && TURNSTILE_SITEKEY === '1x00000000000000000000AA') {
    return (
      <div className="p-4 bg-yellow-50 dark:bg-yellow-900/30 rounded-lg text-center">
        <p className="text-sm text-yellow-700 dark:text-yellow-300">
          Dev Mode: Captcha bypassed
        </p>
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      {isLoading && (
        <div className="w-[300px] h-[65px] bg-neutral-100 dark:bg-neutral-800 rounded animate-pulse flex items-center justify-center">
          <span className="text-sm text-neutral-500">Loading captcha...</span>
        </div>
      )}
      <div ref={containerRef} className={isLoading ? 'hidden' : ''} />
    </div>
  );
}
